import React, { useMemo, useState } from 'react';
import CTAButton from '../../shared/CTAButton';
import { useFadeUp } from '../../shared/useFadeUp';
import {
  FALLBACK_SOLO_PRICES,
  formatCents,
  savePercent,
  soloDisplay,
  trialLabel,
} from './soloPrice';
import { useSoloPrices } from './useSoloPrices';
import './pricing.css';

type Billing = 'monthly' | 'yearly';

interface PricingProps {
  discountFactor?: number;
  discountMessage?: string;
}

const scrollToDownload = () =>
  document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });

interface Plan {
  key: string;
  eyebrow: string;
  monthlyCents: number | 'contact';
  priceLabel?: string;
  yearlyCents?: number;
  unit?: string;
  audience: string;
  features: string[];
  includesPrev?: string;
  ctaLabel: string;
  ctaDisabled?: boolean;
  featured?: boolean;
  annualCents?: number;
  savePct?: number;
  trialMonthly?: string;
  trialYearly?: string;
}

const PLANS: Plan[] = [
  {
    key: 'free',
    eyebrow: 'Free',
    monthlyCents: 0,
    audience: 'For inspectors trying Morph before committing.',
    features: [
      'AI Report Generation (5/month)',
      'Voice-to-AI-Polished Text',
      '1-Click Report Export',
      'Customer Support',
      'Self-Serve Onboarding',
    ],
    ctaLabel: 'Get Started Free',
  },
  {
    key: 'solo',
    eyebrow: 'Solo',
    monthlyCents: FALLBACK_SOLO_PRICES.monthlyCents, // overridden at render by the price API set
    unit: '/mo',
    audience: 'For solo inspectors running their own business.',
    includesPrev: 'Everything in Free, plus:',
    features: [
      'AI Report Generation (Unlimited)',
      'Smart Template Import',
      'Personalized AI Style',
      'Email and Chat Support',
      'Scheduled Onboarding',
    ],
    ctaLabel: 'Start Free Trial',
    featured: true,
  },
  {
    key: 'team',
    eyebrow: 'Team',
    monthlyCents: 'contact',
    priceLabel: 'Coming 2027',
    audience: 'For firms with multiple inspectors.',
    includesPrev: 'Everything in Solo, plus:',
    features: [
      'Team Collaboration',
      'CRM Integration',
      'AI Automation',
      'Contract & Payment Management',
      'Multi-Inspector Support',
    ],
    ctaLabel: 'Coming Soon',
    ctaDisabled: true,
  },
  {
    key: 'enterprise',
    eyebrow: 'Enterprise',
    monthlyCents: 'contact',
    audience: 'For large firms and franchise operations.',
    includesPrev: 'Everything in Team, plus:',
    features: [
      'Advanced CRM Integration',
      'Advanced AI Automation',
      'Customized Report Styles',
      'Full API Access',
      'Dedicated Account Manager',
      'In-person Setup & Training',
    ],
    ctaLabel: 'Coming Soon',
    ctaDisabled: true,
  },
];

const Pricing: React.FC<PricingProps> = ({
  discountFactor = 1,
  discountMessage,
}) => {
  const [billing, setBilling] = useState<Billing>('yearly');
  const head = useFadeUp<HTMLDivElement>();

  const prices = useSoloPrices();

  const plans = useMemo(() => {
    const solo = soloDisplay(prices, discountFactor);
    const save = savePercent(prices);
    return PLANS.map((p) => {
      if (p.key === 'solo') {
        return {
          ...p,
          monthlyCents: solo.monthlyCents,
          yearlyCents: solo.yearlyPerMonthCents,
          annualCents: solo.annualCents,
          savePct: save > 0 ? save : undefined,
          trialMonthly: trialLabel(prices.monthlyTrialDays),
          trialYearly: trialLabel(prices.yearlyTrialDays),
        };
      }
      return p;
    });
  }, [prices, discountFactor]);

  return (
    <section className="v2-pricing" id="pricing">
      <div className="v2-pricing__container">
        <div
          ref={head.ref}
          className={`v2-pricing__head fade-up ${head.isVisible ? 'is-visible' : ''}`}
        >
          <h2 className="ui-h2">Pick a plan. Your first job is free.</h2>
          <p className="ui-body">
            One plan for solo inspectors. One for your team.
          </p>
          {discountMessage && (
            <div className="v2-pricing__discount">{discountMessage}</div>
          )}
          <div className="v2-pricing__toggle" role="tablist">
            <button
              type="button"
              className={`v2-pricing__toggle-btn ${billing === 'monthly' ? 'is-active' : ''}`}
              onClick={() => setBilling('monthly')}
              role="tab"
              aria-selected={billing === 'monthly'}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`v2-pricing__toggle-btn ${billing === 'yearly' ? 'is-active' : ''}`}
              onClick={() => setBilling('yearly')}
              role="tab"
              aria-selected={billing === 'yearly'}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="v2-pricing__cards">
          {plans.map((p) => {
            const priceCents =
              billing === 'yearly' && p.yearlyCents !== undefined
                ? p.yearlyCents
                : (p.monthlyCents as number | undefined);
            const trialText =
              billing === 'yearly' ? p.trialYearly : p.trialMonthly;
            const annualNote =
              billing === 'yearly' && p.annualCents
                ? `${formatCents(p.annualCents)} billed annually`
                : '';

            const isContact = p.monthlyCents === 'contact';

            return (
              <div
                key={p.key}
                className={`v2-pricing__card ${p.featured ? 'v2-pricing__card--featured' : ''}`}
              >
                {p.featured && (
                  <div className="v2-pricing__badge">Most popular</div>
                )}
                <div className="ui-eyebrow">{p.eyebrow}</div>
                <div className="v2-pricing__price">
                  {isContact ? (
                    <span className="v2-pricing__price-contact">
                      {p.priceLabel ?? 'Contact Us'}
                    </span>
                  ) : (
                    <>
                      <strong>{formatCents(priceCents ?? 0)}</strong>
                      <span className="v2-pricing__unit">
                        {p.unit ?? '/mo'}
                      </span>
                      {billing === 'yearly' && p.savePct !== undefined && (
                        <span className="v2-pricing__save">{`Save ~${p.savePct}%`}</span>
                      )}
                    </>
                  )}
                </div>
                <div className="v2-pricing__billing-note">
                  {annualNote || ' '}
                </div>
                <div
                  className={`v2-pricing__trial ${billing === 'yearly' && p.trialYearly ? 'v2-pricing__trial--emphasized' : ''}`}
                >
                  {trialText || ' '}
                </div>
                <p className="v2-pricing__audience">{p.audience}</p>
                <ul className="v2-pricing__list">
                  {p.includesPrev && (
                    <li className="v2-pricing__list-include">
                      {p.includesPrev}
                    </li>
                  )}
                  {p.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <CTAButton
                  variant="blue"
                  size="lg"
                  disabled={p.ctaDisabled}
                  onClick={p.ctaDisabled ? undefined : scrollToDownload}
                  className="v2-pricing__cta"
                >
                  {p.ctaLabel}
                </CTAButton>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
